/* Global Branch Controller (Dashboard as source of truth) */
import AllActionTypes from '../Utility/AllActionTypes';
import { REHYDRATE } from 'redux-persist';

/*
 * State shape:
 *  globalBranch     -> the Dashboard-selected branch (single source of truth)
 *  branches         -> shared list of available branches
 *  dashboardVersion -> incremented every time the Dashboard branch changes.
 *                      Used to invalidate stale module-level overrides.
 *  moduleBranches   -> { [moduleId]: { branch, version } } local overrides.
 *                      An override is only "live" while its version matches
 *                      the current dashboardVersion.
 */
const initialState = {
    globalBranch: {},
    branches: [],
    dashboardVersion: 0,
    moduleBranches: {}
};

/* Guard against stale serialized JSX in persisted storage — labels must be strings. */
const isEmptyBranch = (branch) =>
    !branch || typeof branch !== 'object' || (!branch.value && !branch.id);

const sanitizeBranch = (branch) => {
    if (isEmptyBranch(branch)) return {};
    return {
        ...branch,
        label: typeof branch.label === 'string'
            ? branch.label
            : String(branch.branch_name || branch.id || branch.value || '')
    };
};

export default (state = initialState, action) => {
    switch (action.type) {
        /*
         * On rehydrate, keep the persisted globalBranch/version/moduleBranches
         * but clear the cached branch list so the Dashboard refetches a fresh
         * list and re-validates the selection on mount.
         */
        case REHYDRATE:
            if (action.key === 'root' && action.payload?.GlobalBranchReducer) {
                const persisted = action.payload.GlobalBranchReducer;
                return {
                    ...state,
                    globalBranch: sanitizeBranch(persisted.globalBranch),
                    dashboardVersion: persisted.dashboardVersion || 0,
                    moduleBranches: persisted.moduleBranches || {},
                    branches: [] // force fresh fetch
                };
            }
            return state;

        case AllActionTypes.SET_GLOBAL_BRANCHES:
            return {
                ...state,
                branches: (action.payload || []).map(sanitizeBranch)
            };

        /* Dashboard change: new global branch + bump version to override all modules. */
        case AllActionTypes.SET_DASHBOARD_BRANCH:
            return {
                ...state,
                globalBranch: sanitizeBranch(action.payload || {}),
                dashboardVersion: state.dashboardVersion + 1
            };

        /* Module change: store override stamped with the current dashboard version. */
        case AllActionTypes.SET_MODULE_BRANCH:
            return {
                ...state,
                moduleBranches: {
                    ...state.moduleBranches,
                    [action.payload.moduleId]: {
                        branch: sanitizeBranch(action.payload.branch || {}),
                        version: state.dashboardVersion
                    }
                }
            };

        default:
            return state;
    }
};
